import type { StaffRole, StaffRoleType } from '../types';

export type BulkRoleMode = 'add' | 'replace';

/** Reviewer intent for a bulk edit — keep existing / clear / set to a specific user. */
export type BulkReviewerChange =
  | { type: 'keep' }
  | { type: 'clear' }
  | { type: 'set'; reviewerId: string };

export interface BulkEditParams {
  /** Roles chosen in the dialog; an empty list means "leave roles untouched". */
  roles: StaffRoleType[];
  mode: BulkRoleMode;
  reviewer: BulkReviewerChange;
  markInactive: boolean;
}

export interface BulkEditResult {
  roles: StaffRoleType[];
  reviewerId: string | undefined;
  isActive: boolean;
}

/**
 * Computes the post-bulk-edit roles + reviewer mapping for one user.
 *
 * - `markInactive` wins: clears roles and deactivates.
 * - `add` merges the chosen roles into the user's existing roles (deduped);
 *   `replace` swaps them outright.
 * - An empty role selection leaves the user's roles untouched, so a reviewer-only
 *   bulk edit is possible without disturbing roles.
 * - `reviewerId` only applies to preparers; it is dropped for anyone whose resulting
 *   roles don't include `preparer`.
 */
export function computeBulkRoleUpdate(
  current: Pick<StaffRole, 'roles' | 'reviewerId'>,
  params: BulkEditParams,
): BulkEditResult {
  if (params.markInactive) {
    return { roles: [], reviewerId: undefined, isActive: false };
  }

  let roles: StaffRoleType[];
  if (params.roles.length === 0) {
    roles = current.roles;
  } else if (params.mode === 'replace') {
    roles = params.roles;
  } else {
    roles = Array.from(new Set([...current.roles, ...params.roles]));
  }

  let reviewerId: string | undefined;
  if (!roles.includes('preparer')) {
    reviewerId = undefined;
  } else if (params.reviewer.type === 'keep') {
    reviewerId = current.reviewerId;
  } else if (params.reviewer.type === 'clear') {
    reviewerId = undefined;
  } else {
    reviewerId = params.reviewer.reviewerId;
  }

  return { roles, reviewerId, isActive: true };
}
